import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { useApiQuery } from '../useApiQuery';
import { apiDelete, apiGet, apiPost, formsEngineEndpoints, operationsClient } from '@/lib/api';
import { formsEngineKeys } from '@/lib/queryKeys';
import type {
  CreateQuestionInput,
  CreateQuestionVersionInput,
  QuestionDefinition,
  QuestionVersion,
} from '@/types/formsEngine';

export interface QuestionBankFilters {
  search?: string;
  type?: string;
  status?: 'ACTIVE' | 'ARCHIVED';
}

export function useQuestionBank(filters: QuestionBankFilters = {}) {
  const params = {
    status: filters.status ?? 'ACTIVE',
    ...(filters.search?.trim() ? { search: filters.search.trim() } : {}),
    ...(filters.type ? { type: filters.type } : {}),
  };

  return useApiQuery<QuestionDefinition[]>({
    queryKey: formsEngineKeys.questions(params),
    queryFn: () =>
      apiGet<QuestionDefinition[]>(operationsClient, formsEngineEndpoints.questions(), { params }),
    showErrorToast: false,
  });
}

export function useCreateQuestion() {
  const queryClient = useQueryClient();

  return useApiMutation<QuestionDefinition, CreateQuestionInput>({
    mutationFn: (input) =>
      apiPost<QuestionDefinition, CreateQuestionInput>(
        operationsClient,
        formsEngineEndpoints.questions(),
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: formsEngineKeys.all() });
      toast.success('Question added to the bank');
    },
  });
}

export function useUpdateAndPublishQuestion() {
  const queryClient = useQueryClient();

  return useApiMutation<
    QuestionDefinition,
    { questionId: string; input: CreateQuestionVersionInput }
  >({
    mutationFn: async ({ questionId, input }) => {
      const draft = await apiPost<QuestionVersion, CreateQuestionVersionInput>(
        operationsClient,
        formsEngineEndpoints.questionVersions(questionId),
        input,
      );
      try {
        await apiPost<QuestionVersion, { versionId: string }>(
          operationsClient,
          formsEngineEndpoints.publishQuestion(questionId),
          { versionId: draft.id },
        );
      } catch (error) {
        await apiDelete(
          operationsClient,
          formsEngineEndpoints.questionVersion(questionId, draft.id),
        ).catch(() => undefined);
        throw error;
      }
      return apiGet<QuestionDefinition>(
        operationsClient,
        formsEngineEndpoints.question(questionId),
      );
    },
    onSuccess: (question) => {
      void queryClient.invalidateQueries({ queryKey: formsEngineKeys.all() });
      toast.success(`Question updated to v${question.currentVersion}`);
    },
  });
}

function useQuestionStatusMutation(action: 'archive' | 'restore') {
  const queryClient = useQueryClient();

  return useApiMutation<QuestionDefinition, string>({
    mutationFn: (questionId) =>
      apiPost<QuestionDefinition>(
        operationsClient,
        action === 'archive'
          ? formsEngineEndpoints.archiveQuestion(questionId)
          : formsEngineEndpoints.restoreQuestion(questionId),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: formsEngineKeys.all() });
      toast.success(action === 'archive' ? 'Question archived' : 'Question restored');
    },
  });
}

export function useArchiveQuestion() {
  return useQuestionStatusMutation('archive');
}

export function useRestoreQuestion() {
  return useQuestionStatusMutation('restore');
}
